/**
 * Signage device fleet resources — types from `@navio/nsdk`.
 *
 * Hybrid delivery: product API enqueues, then POSTs to Heimdall. The Pi pulls
 * Heimdall's queue only — mesh miss is a fallback after the hub has the row.
 */

import type {
  SignageCommandCancelApiData,
  SignageCommandCancelApiResponses,
  SignageCommandStatusApiData,
  SignageCommandStatusApiResponses,
  SignageDeviceCommandApiData,
  SignageDeviceCommandApiResponses,
  SignageDeviceCommandLogListApiData,
  SignageDeviceCommandLogListApiResponses,
  SignageDeviceDeleteApiData,
  SignageDeviceDeleteApiResponses,
  SignageDeviceDisplayApiData,
  SignageDeviceDisplayApiResponses,
  SignageDeviceGetApiData,
  SignageDeviceGetApiResponses,
  SignageDeviceGrafanaKioskDeleteApiData,
  SignageDeviceGrafanaKioskDeleteApiResponses,
  SignageDeviceGrafanaKioskGetApiData,
  SignageDeviceGrafanaKioskGetApiResponses,
  SignageDeviceGrafanaKioskPostApiData,
  SignageDeviceGrafanaKioskPostApiResponses,
  SignageDeviceGrafanaPublicDashboardsApiData,
  SignageDeviceGrafanaPublicDashboardsApiResponses,
  SignageDeviceListApiData,
  SignageDeviceListApiResponses,
  SignageDevicePairApiData,
  SignageDevicePairApiResponses,
  SignageDevicePatchApiData,
  SignageDevicePatchApiResponses,
  SignageDevicePatchInput,
  SignageDeviceRecord,
  SignageDeviceRelayStatusApiData,
  SignageDeviceRelayStatusApiResponses,
  SignageDeviceUnpairApiData,
  SignageDeviceUnpairApiResponses,
  SignageDeviceUpdateAllApiData,
  SignageDeviceUpdateAllApiResponses,
  SignageInstalledAppRecord
} from '@navio/nsdk';

import type { ApiInput } from '../../utils';
import { apiUrl, dataEndpoint, responseEndpoint } from '../defineResource';
import { getSignageFleetUrls } from './fleetUrls';

export type {
  SignageAppReleaseRecord,
  SignageBulkAssignInput,
  SignageBulkAssignResultRecord,
  SignageBulkCommandInput,
  SignageBulkCommandResultRecord,
  SignageCecControlCapabilityRecord,
  SignageCommandCancelResultRecord,
  SignageCommandLifecycleRecord,
  /** Command log audit row (nsdk includes deliveryChannel + cancelled status). */
  SignageCommandLogRecord,
  SignageCommandLogStatus,
  SignageCommandType,
  SignageDeviceDisplayStateRecord,
  /** Staff-facing device record (activeDisplay / installedApps from OS heartbeat). */
  SignageDeviceRecord,
  SignageDeviceRelayStatusRecord,
  SignageDisplayCecControlsRecord,
  SignageDisplayInfoRecord,
  SignageFleetStatsRecord,
  SignageGrafanaKioskAssignInput,
  SignageGrafanaKioskAssignmentRecord,
  SignageGrafanaPublicDashboardListRecord,
  SignageGrafanaPublicDashboardRecord,
  SignageGroupRecord,
  SignageInstalledAppRecord,
  SignageOnlineStatus,
  SignageOsReleaseRecord,
  SignageReleaseCatalogRecord,
  SignageRolloutRecord,
  SignageRolloutStatus,
  SignageUpdateAllItemRecord,
  SignageUpdateAllResultRecord
} from '@navio/nsdk';

/** Known exclusive-screen app ids used as switch_display targets when telemetry is sparse. */
export const SIGNAGE_KNOWN_DISPLAY_APPS = ['signage', 'grafana-kiosk', 'labelproxy-docs'] as const;

const KNOWN_DISPLAY_APP_SET = new Set<string>([...SIGNAGE_KNOWN_DISPLAY_APPS, 'kiosk']);

export const SIGNAGE_GRAFANA_KIOSK_APP = 'grafana-kiosk';

export const signageDeviceActiveDisplay = (device: SignageDeviceRecord): string => (device.activeDisplay ?? '').trim();

export const isSignageGrafanaKioskApp = (appId: string | null | undefined): boolean =>
  appId?.trim().toLowerCase() === SIGNAGE_GRAFANA_KIOSK_APP;

export const isSignageGrafanaKiosk = (device: SignageDeviceRecord): boolean =>
  isSignageGrafanaKioskApp(signageDeviceActiveDisplay(device));

/** Display-kind apps reported on the last OS heartbeat. */
export const signageDeviceDisplayApps = (device: SignageDeviceRecord): SignageInstalledAppRecord[] =>
  (device.installedApps ?? []).filter((row) => {
    if (!row.app?.trim()) {
      return false;
    }
    const kind = (row.kind ?? '').toLowerCase();
    if (kind === 'display') {
      return true;
    }
    // Older agents omit kind — still allow known display app ids.
    return kind === '' && KNOWN_DISPLAY_APP_SET.has(row.app);
  });

/**
 * switch_display targets for the staff app switcher.
 * Prefer installed display apps (with versions); always include known display apps
 * and the current active display so the control works before/without telemetry.
 */
export const signageDeviceSwitchDisplayTargets = (device: SignageDeviceRecord): SignageInstalledAppRecord[] => {
  const byApp = new Map<string, SignageInstalledAppRecord>();

  for (const appId of SIGNAGE_KNOWN_DISPLAY_APPS) {
    byApp.set(appId, { app: appId, kind: 'display' });
  }

  for (const row of signageDeviceDisplayApps(device)) {
    byApp.set(row.app, row);
  }

  const active = signageDeviceActiveDisplay(device);
  if (active && !byApp.has(active)) {
    byApp.set(active, { app: active, kind: 'display' });
  }

  return [...byApp.values()];
};

/** pushed = mesh accepted; queued = on Heimdall (mesh or next heartbeat pull). */
export type SignageCommandDelivery = 'pushed' | 'queued';

/**
 * nsdk PATCH input omits null on clearable content fields even though the API accepts null.
 * Keep the rest of the generated shape; only widen clearable fields.
 * assignedUrl must be null (not "") — backend URL validation rejects empty strings.
 */
export type SignageDevicePatchBody = Omit<
  SignageDevicePatchInput,
  'playlistId' | 'dashboardId' | 'contentPresetId' | 'assignedUrl'
> & {
  playlistId?: number | null;
  dashboardId?: number | null;
  contentPresetId?: number | null;
  assignedUrl?: string | null;
};

export type SignageDevicePatchRequest = Omit<ApiInput<SignageDevicePatchApiData>, keyof SignageDevicePatchInput> &
  SignageDevicePatchBody;

/** Closed set of CEC control capability statuses from the RPi probe. */
export type SignageCecControlStatus = 'supported' | 'likely' | 'unknown' | 'unsupported' | 'blocked' | 'unavailable';

const CEC_CONTROL_STATUSES = new Set<string>([
  'supported',
  'likely',
  'unknown',
  'unsupported',
  'blocked',
  'unavailable'
]);

/** Normalize probe status strings to the closed capability enum. */
export const parseSignageCecControlStatus = (raw: string | null | undefined): SignageCecControlStatus | null => {
  if (!raw) {
    return null;
  }
  const status = raw.trim().toLowerCase();
  return CEC_CONTROL_STATUSES.has(status) ? (status as SignageCecControlStatus) : null;
};

export const getDeviceUrls = {
  getPaginatedSignageDevices: dataEndpoint<SignageDeviceListApiData, SignageDeviceListApiResponses>({
    getUrl: (queryParams) => apiUrl('devices/signage/', queryParams)
  }),
  getSignageDevice: dataEndpoint<SignageDeviceGetApiData, SignageDeviceGetApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/`)
  }),
  getSignageDeviceCommandLogs: dataEndpoint<
    SignageDeviceCommandLogListApiData,
    SignageDeviceCommandLogListApiResponses
  >({
    getUrl: ({ pk, ...queryParams }) => apiUrl(`devices/signage/${pk}/command-logs/`, queryParams)
  }),
  /**
   * Probe Heimdall mesh reachability for this device.
   * Path stays `relay-status/` for backend compatibility (Heimdall replaces fleet-relay).
   */
  getSignageDeviceRelayStatus: dataEndpoint<SignageDeviceRelayStatusApiData, SignageDeviceRelayStatusApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/relay-status/`)
  }),
  /**
   * Product command log + best-effort Heimdall hub queue row.
   */
  getSignageCommandStatus: dataEndpoint<SignageCommandStatusApiData, SignageCommandStatusApiResponses>({
    getUrl: ({ commandId }) => apiUrl(`devices/signage/commands/${commandId}/`)
  }),
  /**
   * Live active display from Keel GET /display (proxied by backend).
   * Prefer over heartbeat activeDisplay when the mesh is up.
   */
  getSignageDeviceDisplay: dataEndpoint<SignageDeviceDisplayApiData, SignageDeviceDisplayApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/display/`)
  }),
  /**
   * Public-dashboard catalog from the device grafana-kiosk OTA package (Keel).
   * Not a direct Grafana Cloud integration.
   */
  getSignageDeviceGrafanaPublicDashboards: dataEndpoint<
    SignageDeviceGrafanaPublicDashboardsApiData,
    SignageDeviceGrafanaPublicDashboardsApiResponses
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/grafana-kiosk/public-dashboards/`)
  }),
  getSignageDeviceGrafanaKiosk: dataEndpoint<
    SignageDeviceGrafanaKioskGetApiData,
    SignageDeviceGrafanaKioskGetApiResponses
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/grafana-kiosk/kiosk/`)
  }),
  postSignageDeviceGrafanaKiosk: responseEndpoint<
    SignageDeviceGrafanaKioskPostApiData,
    SignageDeviceGrafanaKioskPostApiResponses
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/grafana-kiosk/kiosk/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteSignageDeviceGrafanaKiosk: dataEndpoint<
    SignageDeviceGrafanaKioskDeleteApiData,
    SignageDeviceGrafanaKioskDeleteApiResponses,
    { pk: number }
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/grafana-kiosk/kiosk/`)
  }),
  ...getSignageFleetUrls,
  postSignageDevicePair: responseEndpoint<SignageDevicePairApiData, SignageDevicePairApiResponses>({
    getUrl: () => apiUrl('devices/signage/pair/'),
    getBody: (data) => data
  }),
  postSignageDeviceCommand: responseEndpoint<SignageDeviceCommandApiData, SignageDeviceCommandApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/commands/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  /**
   * Cancel a queued command on product + best-effort on Heimdall.
   */
  postSignageCommandCancel: responseEndpoint<SignageCommandCancelApiData, SignageCommandCancelApiResponses>({
    getUrl: ({ commandId }) => apiUrl(`devices/signage/commands/${commandId}/cancel/`)
  }),
  /**
   * Enqueue every package this device needs (Keel platform first, then apps).
   * Body-less POST; backend compares heartbeat versions to the release catalog.
   * Only the first step attempts Heimdall push so later packages stay FIFO.
   */
  postSignageDeviceUpdateAll: responseEndpoint<
    SignageDeviceUpdateAllApiData,
    SignageDeviceUpdateAllApiResponses,
    { pk: number }
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/commands/update-all/`)
  }),
  postSignageDeviceUnpair: responseEndpoint<SignageDeviceUnpairApiData, SignageDeviceUnpairApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/unpair/`)
  }),
  patchSignageDevice: responseEndpoint<
    SignageDevicePatchApiData,
    SignageDevicePatchApiResponses,
    SignageDevicePatchRequest
  >({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/`),
    getBody: ({ pk: _, ...body }) => body
  }),
  deleteSignageDevice: dataEndpoint<SignageDeviceDeleteApiData, SignageDeviceDeleteApiResponses>({
    getUrl: ({ pk }) => apiUrl(`devices/signage/${pk}/`)
  })
};
